import { type ScriptableTooltipContext, type TooltipModel } from "chart.js";
import { groupBy } from "lodash";
import moment from "moment";

import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { MetricFormatters } from "@north-beam/nb-client-side";

export function chartTooltip(context: ScriptableTooltipContext<"line">) {
  const { chart } = context;
  const { tooltip } = context;
  const { options }: any = context.tooltip;

  let tooltipEl = chart.canvas?.parentNode?.querySelector("div");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    tooltipEl.style.zIndex = "3000";
    chart.canvas.parentNode?.appendChild(tooltipEl);
  }

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  if (tooltip.body && tooltip.dataPoints.length) {
    const content = buildTooltipContent(tooltip, options);
    const date = tooltip.dataPoints[0].label;

    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    const { dataIndex } = tooltip.dataPoints[0];
    const weekDatum = options.selectedBudgetMix?.data.weekly_data?.[dataIndex];
    const displayDate = weekDatum
      ? `${moment(weekDatum.start_date).format("MMM DD")} - ${moment(
          weekDatum.end_date,
        )
          .subtract(1, "days")
          .format("MMM DD")}`
      : date;

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          style="min-width: 330px;"
          class="
            bg-v2-primary border border-v2-secondary py-5 px-4 rounded-md space-y-2.5
            shadow whitespace-nowrap ${contentPosition}
          "
        >
          <div class="text-lg font-bold leading-0 py-2">${displayDate}</div>
          ${content}
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    left: positionX + tooltip.caretX + "px",
    top: positionY + tooltip.caretY + "px",
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");
      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");
      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const buildTooltipContent = (
  tooltip: TooltipModel<"line" | "bar">,
  options: any,
) => {
  const content: string[] = [];

  const series = groupBy(tooltip.dataPoints, (e) => (e.dataset as any).series);

  Object.entries(series).forEach(([, seriesItem]) => {
    let result = `
      <div class="mt-3">
        <div class="font-bold mb-2">${options.selectedBudgetMix.name}</div>
    `;

    seriesItem.forEach(({ dataset, parsed }) => {
      let value = options.isRevenue
        ? MetricFormatters.currency(
            parsed.y,
            options.user.currencies.currentCurrency,
            { precision: "exact" },
          )
        : MetricFormatters.decimal(
            parsed.y,
            options.user.currencies.currentCurrency,
            { precision: "exact" },
          );

      if (dataset.label === "Spend") {
        value = MetricFormatters.currency(
          parsed.y,
          options.user.currencies.currentCurrency,
          { precision: "exact" },
        );
      }

      const legend = buildLegend(
        {
          borderColor:
            dataset.label === "Low"
              ? dataset.backgroundColor
              : dataset.borderColor,
          backgroundColor: dataset.backgroundColor,
          borderDash: dataset.borderDash ?? [],
          borderWidth: dataset.label === "Low" ? 0 : 4,
        } as any,
        dataset.label === "Low",
      );

      result += `
        <div class="flex items-center justify-between space-y-1">
          <div class="flex items-center gap-2">
            <div style="width: 22px;">${
              dataset.label !== "High" ? legend : ""
            }</div>
            <div>${
              dataset.label === "Low"
                ? "Forecast Range (low → high)"
                : dataset.label === "High"
                ? ""
                : dataset.label
            }</div>
          </div>
          <div class="font-mono">${value}</div>
        </div>
      `;
    });

    result += "</div>";

    content.push(result);
  });

  const divider = `<div class="${"border-t border-v2-secondary h-1 mt-3"}"></div>`;

  return `
    <div class="text-xs">
      ${divider}
      ${content.join(divider)}
    </div>
  `;
};
